import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { adminService } from '@services/adminService'

function isCanceledRequest(error) {
  return (
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError' ||
    error?.code === 'ERR_CANCELED'
  )
}

function useAdminResource({
  enabled = true,
  identity,
  request,
  emptyMessage
}) {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(enabled)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshCycle, setRefreshCycle] = useState(0)
  const requestRef = useRef(request)
  const dataRef = useRef(null)
  const identityRef = useRef(null)
  const sequenceRef = useRef(0)

  requestRef.current = request

  useEffect(() => {
    if (!enabled) {
      identityRef.current = null
      dataRef.current = null
      setData(null)
      setError(null)
      setIsLoading(false)
      setIsRefreshing(false)
      return undefined
    }

    const identityChanged = identityRef.current !== identity
    identityRef.current = identity
    const hasSnapshot = !identityChanged && Boolean(dataRef.current)
    if (identityChanged) {
      dataRef.current = null
      setData(null)
    }

    const controller = new AbortController()
    const sequence = sequenceRef.current + 1
    sequenceRef.current = sequence
    setError(null)
    setIsLoading(!hasSnapshot)
    setIsRefreshing(hasSnapshot)

    const load = async () => {
      try {
        const response = await requestRef.current(controller.signal)
        if (controller.signal.aborted || sequence !== sequenceRef.current) return

        const payload = response?.data
        if (!payload) throw new Error(emptyMessage)
        dataRef.current = payload
        setData(payload)
      } catch (requestError) {
        if (
          controller.signal.aborted ||
          isCanceledRequest(requestError) ||
          sequence !== sequenceRef.current
        ) {
          return
        }
        setError(requestError)
      } finally {
        if (!controller.signal.aborted && sequence === sequenceRef.current) {
          setIsLoading(false)
          setIsRefreshing(false)
        }
      }
    }

    load()
    return () => {
      sequenceRef.current += 1
      controller.abort()
    }
  }, [emptyMessage, enabled, identity, refreshCycle])

  const refresh = useCallback(() => {
    setRefreshCycle(current => current + 1)
  }, [])

  return {
    data,
    error,
    isLoading,
    isRefreshing,
    refresh
  }
}

function useQueryResource({
  enabled,
  query,
  request,
  emptyMessage
}) {
  const identity = useMemo(() => JSON.stringify(query), [query])

  return useAdminResource({
    enabled,
    identity,
    request,
    emptyMessage
  })
}

export function useAdminEvents(query, enabled = true) {
  return useQueryResource({
    enabled,
    query,
    request: signal => adminService.getAnalyticsEvents(query, { signal }),
    emptyMessage: 'Admin event explorer returned no data'
  })
}

export function useAdminEvent(eventId, enabled = true) {
  return useAdminResource({
    enabled: enabled && Boolean(eventId),
    identity: eventId || 'none',
    request: signal =>
      adminService.getAnalyticsEvent(eventId, { signal }),
    emptyMessage: 'Admin event detail returned no data'
  })
}

export function useAdminCorrelationEvents(correlation, enabled = true) {
  const type = correlation?.type
  const id = correlation?.id

  return useAdminResource({
    enabled: enabled && Boolean(type && id),
    identity: type && id ? `${type}:${id}` : 'none',
    request: signal =>
      type === 'trace'
        ? adminService.getAnalyticsTraceEvents(id, { signal })
        : adminService.getAnalyticsRequestEvents(id, { signal }),
    emptyMessage: 'Admin event correlation returned no data'
  })
}

export function useAdminDependencies(query, enabled = true) {
  return useQueryResource({
    enabled,
    query,
    request: signal =>
      adminService.getAnalyticsDependencies(query, { signal }),
    emptyMessage: 'Dependency analytics returned no data'
  })
}

export function useAdminAuthentication(query, enabled = true) {
  return useQueryResource({
    enabled,
    query,
    request: signal =>
      adminService.getAnalyticsAuthentication(query, { signal }),
    emptyMessage: 'Authentication analytics returned no data'
  })
}

function isObservabilitySnapshot(value) {
  return (
    value &&
    typeof value.generatedAt === 'string' &&
    typeof value.state === 'string' &&
    typeof value.window?.id === 'string' &&
    value.summary &&
    Array.isArray(value.alerts) &&
    Array.isArray(value.pipelines) &&
    Array.isArray(value.tools) &&
    Array.isArray(value.coverage?.services) &&
    Array.isArray(value.dataQuality?.sources)
  )
}

export function useAdminObservability(
  windowId = '1h',
  enabled = true
) {
  if (typeof windowId === 'boolean') {
    enabled = windowId
    windowId = '1h'
  }

  const resource = useAdminResource({
    enabled,
    identity: `observability:${windowId}`,
    request: async signal => {
      const response =
        await adminService.getOperationsObservabilityOverview(
          { window: windowId },
          { signal }
        )
      if (!isObservabilitySnapshot(response?.data)) {
        throw new Error(
          'Admin observability returned an invalid snapshot'
        )
      }
      return response
    },
    emptyMessage: 'Admin observability status returned no data'
  })
  const { data, refresh } = resource

  useEffect(() => {
    if (!enabled || !data) return undefined

    const seconds = Number(
      data.refreshIntervalSeconds ||
        data.autoRefreshSeconds
    )
    const delay =
      (Number.isFinite(seconds) && seconds > 0 ? seconds : 30) *
      (0.9 + Math.random() * 0.2) *
      1000
    let timer

    const schedule = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        if (document.hidden) {
          schedule()
          return
        }
        refresh()
      }, delay)
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) refresh()
    }

    schedule()
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.clearTimeout(timer)
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      )
    }
  }, [data, enabled, refresh])

  return resource
}
